import { AsyncPipe, NgClass } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { NavigationEnd, Router, RouterOutlet } from '@angular/router'

import { takeUntil } from 'rxjs'

import { BackButtonDispatcher, NavigationHistoryProvider } from '@ti/core/common/services'
import { BrandingService } from '@ti/core/common/services/branding.service'
import { injectDestroy$ } from '@ti/core/common/utils'
import { ChevronIconComponent } from '@ti/core/icons/chevron/chevron-icon.component'
import { SidebarPanelService } from '@ti/core/ui-kit/layout/components/sidebar-panel/sidebar-panel.service'
import { SidebarService } from '@ti/core/ui-kit/layout/services/sidebar.service'
import { AssistantDialogComponent } from '@ti/shared/assistant/components/dialog'
import { AssistantChatFacade } from '@ti/shared/assistant/services/assistant-chat.facade'
import { IntercomComponent } from '@ti/shared/fleet/components/intercom'
import { IntercomViewModel } from '@ti/shared/fleet/models/intercom-view.model'

import { SidebarComponent } from '../sidebar/sidebar.component'

@Component({
  selector: 'app-layout',
  templateUrl: 'layout.component.html',
  styleUrls: ['layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    ChevronIconComponent,
    NgClass,
    RouterOutlet,
    SidebarComponent,
    AssistantDialogComponent,
    IntercomComponent,
  ],
})
export class LayoutComponent implements OnInit, AfterViewInit {
  protected readonly backButton = inject(BackButtonDispatcher)
  protected readonly logoService = inject(BrandingService)
  protected readonly navigationHistoryProvider = inject(NavigationHistoryProvider)
  protected readonly router = inject(Router)
  protected readonly sidebarPanelService = inject(SidebarPanelService)
  protected readonly sidebarService = inject(SidebarService)
  protected readonly assistant = inject(AssistantChatFacade)
  protected readonly intercom = inject(IntercomViewModel)
  protected readonly destroy$ = injectDestroy$()

  /** Horizontal space the sidebar currently takes; the assistant dialog is anchored to it. */
  protected readonly sidebarOffset = signal(0)

  // Only on the phone breakpoint does the sidebar leave the flow (no offset) and overlay the page.
  protected readonly isPhoneLayout = computed(() => this.sidebarOffset() === 0)
  protected readonly isSidebarOverlayOpen = computed(
    () => !this.sidebarService.isHidden() && this.isPhoneLayout(),
  )
  protected readonly isPanelOpened = toSignal(this.sidebarPanelService.isPanelOpened$, {
    initialValue: false,
  })

  protected readonly sidebarSection = viewChild<ElementRef<HTMLElement>>('sidebarSection')
  protected readonly contentSection = viewChild<ElementRef<HTMLElement>>('contentSection')

  constructor() {
    // The Android back button closes the side menu or the filter panel before it leaves the page
    effect((onCleanup) => {
      if (this.isSidebarOverlayOpen() || this.isPanelOpened()) {
        onCleanup(
          this.backButton.register(() => {
            this.hideOpenedPanel()
            return true
          }),
        )
      }
    })
  }

  public async ngOnInit() {
    this.sidebarService.init()

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.hideOpenedPanel()
      }
    })
  }

  public ngAfterViewInit() {
    this.observeSidebarOffset()
  }

  public hideOpenedPanel() {
    if (!this.sidebarService.isHidden()) {
      this.sidebarService.hide()
    }
    this.sidebarPanelService.closePanel()
  }

  /**
   * The sidebar declares no width: it is content-sized inside the flex row and drops out of the
   * flow entirely on the mobile breakpoint, where it becomes an overlay. The content section's
   * left edge is therefore the only reliable measure of the space it occupies.
   */
  protected observeSidebarOffset() {
    const content = this.contentSection()?.nativeElement
    const sidebar = this.sidebarSection()?.nativeElement
    if (!content || !sidebar) {
      return
    }

    const measure = () => this.sidebarOffset.set(Math.max(0, content.getBoundingClientRect().left))

    const observer = new ResizeObserver(measure)
    observer.observe(content)
    observer.observe(sidebar)
    measure()

    this.destroy$.subscribe(() => observer.disconnect())
  }
}
